import { useState } from "react";
import { useLessonsStatus } from "../../../hooks/useDashboardQueries";
import {
	LessonsStatusPeriod,
	LessonsStatusResponse,
	StatusCounts,
} from "../../../types/dashboard.types";
import { cardStatus } from "../cardStatus";
import DonutChart from "../charts/DonutChart";
import DashboardCard from "../DashboardCard";
import {
	BADGES,
	formatCount,
	formatOwnSplit,
	LESSON_STATUS_SERIES,
	selectableYears,
	yearOptions,
} from "../dashboardConfig";
import ExpandedWidgetModal from "../ExpandedWidgetModal";
import {
	LookupField,
	ProjectField,
	SelectField,
	TenantField,
} from "../FilterControls";
import {
	masterTenantName,
	TenantChoice,
	TenantWidgetProps,
} from "../tenantChoice";
import {
	useDraftFilters,
	useExpandedView,
	useHasOpened,
	useTenantChoice,
} from "../widgetHooks";
import WidgetSelect from "../WidgetSelect";
import WidgetState from "../WidgetState";

const TITLE = "Lessons status";
const EMPTY_TEXT = "No lessons in this period";

const periodOptions = (): Array<{
	value: LessonsStatusPeriod;
	label: string;
}> => [...yearOptions(), { value: "all", label: "All time" }];

const periodLabel = (period: LessonsStatusPeriod) =>
	period === "all" ? "All time" : String(period);

const totalOf = ({ draft, open, closed }: StatusCounts) =>
	draft + open + closed;

const hasNoLessons = (data: LessonsStatusResponse) => totalOf(data) === 0;

interface LessonsStatusChartProps {
	data: LessonsStatusResponse;
	ownLabel: string;
	context: string;
}

function LessonsStatusChart({
	data,
	ownLabel,
	context,
}: LessonsStatusChartProps) {
	const { own } = data;
	const slices = LESSON_STATUS_SERIES.map((status) => ({
		id: status.key,
		label: status.label,
		value: data[status.key],
		color: status.color,
	}));
	const total = totalOf(data);

	return (
		<DonutChart
			slices={slices}
			total={total}
			caption={own ? `${ownLabel} ${formatCount(totalOf(own))}` : undefined}
			leaderLabels
			sliceDetail={
				own
					? (id) =>
							formatOwnSplit(
								ownLabel,
								own[id as keyof StatusCounts],
								data[id as keyof StatusCounts]
							)
					: undefined
			}
			summary={`${TITLE}, ${context}: ${slices
				.map((slice) => `${slice.value} ${slice.label.toLowerCase()}`)
				.join(", ")}; ${total} in total.`}
		/>
	);
}

interface ExpandedProps {
	open: boolean;
	onClose: () => void;
	choice: TenantChoice;
	initialTenant: string;
	initialPeriod: LessonsStatusPeriod;
	ownLabel: string;
}

function LessonsStatusExpanded({
	open,
	onClose,
	choice,
	initialTenant,
	initialPeriod,
	ownLabel,
}: ExpandedProps) {
	const hasOpened = useHasOpened(open);
	const filters = useDraftFilters<{
		tenant: string;
		period: LessonsStatusPeriod;
		project?: string;
		discipline?: string;
	}>({ tenant: initialTenant, period: initialPeriod });
	const { applied, draft } = filters;
	const { target, label } = choice.resolve(applied.tenant);
	const draftTarget = choice.resolve(draft.tenant).target;
	const query = useLessonsStatus(hasOpened ? target : null, applied.period, {
		project: target?.scope === "tenant" ? applied.project : undefined,
		discipline: applied.discipline,
	});
	const context = `${label} · ${periodLabel(applied.period)}`;

	return (
		<ExpandedWidgetModal
			open={open}
			onClose={onClose}
			badge={BADGES.lessons}
			title={TITLE}
			context={context}
			onApply={filters.apply}
			onReset={filters.reset}
			filters={
				<>
					<TenantField
						value={draft.tenant}
						options={choice.options}
						onChange={(tenant) =>
							filters.update({ tenant, project: undefined })
						}
					/>
					<SelectField
						label="Period"
						value={draft.period}
						options={periodOptions()}
						onChange={(period) => filters.update({ period })}
					/>
					{draftTarget?.scope === "tenant" && (
						<ProjectField
							tenantKey={draftTarget.tenantKey}
							value={draft.project}
							onChange={(project) => filters.update({ project })}
						/>
					)}
					<LookupField
						label="Discipline"
						lookup="discipline"
						value={draft.discipline}
						onChange={(discipline) => filters.update({ discipline })}
					/>
				</>
			}
		>
			<WidgetState
				status={cardStatus(query, hasNoLessons)}
				emptyText={EMPTY_TEXT}
				onRetry={query.refetch}
				truncated={query.data?.truncated}
			>
				{query.data && (
					<LessonsStatusChart
						data={query.data}
						ownLabel={ownLabel}
						context={context}
					/>
				)}
			</WidgetState>
		</ExpandedWidgetModal>
	);
}

/** Lessons created in the period, by status. */
function LessonsStatusWidget({ tenants, currentTenantKey }: TenantWidgetProps) {
	const { choice, tenant, setTenant, target, label } = useTenantChoice(
		tenants,
		"master",
		currentTenantKey
	);
	const [period, setPeriod] = useState<LessonsStatusPeriod>(
		() => selectableYears()[0]
	);
	const expanded = useExpandedView();
	const query = useLessonsStatus(target, period);
	const ownLabel = masterTenantName(tenants);

	return (
		<>
			<DashboardCard
				badge={BADGES.lessons}
				title={TITLE}
				className="dashboard-widget--lessons-status dashboard-widget--tall"
				controls={
					<>
						<WidgetSelect
							value={tenant}
							options={choice.options}
							onChange={setTenant}
							ariaLabel={`Tenant for ${TITLE}`}
						/>
						<WidgetSelect
							value={period}
							options={periodOptions()}
							onChange={setPeriod}
							ariaLabel={`Period for ${TITLE}`}
						/>
					</>
				}
				onExpand={expanded.show}
				status={cardStatus(query, hasNoLessons)}
				emptyText={EMPTY_TEXT}
				forbiddenText={`You don't have access to Lessons learned for ${label}.`}
				onRetry={query.refetch}
				truncated={query.data?.truncated}
			>
				{query.data && (
					<LessonsStatusChart
						data={query.data}
						ownLabel={ownLabel}
						context={`${label} · ${periodLabel(period)}`}
					/>
				)}
			</DashboardCard>
			<LessonsStatusExpanded
				key={expanded.session}
				open={expanded.open}
				onClose={expanded.hide}
				choice={choice}
				initialTenant={tenant}
				initialPeriod={period}
				ownLabel={ownLabel}
			/>
		</>
	);
}

export default LessonsStatusWidget;
